import React, { useRef, useState, ReactNode } from 'react'
import { useClickOutsideSingle } from '../../hooks/useClickOutside'
import './TmbControls.css'
import '../../styles/tmbDropdown.css'

interface TmbControlsRowProps {
    children: ReactNode
    className?: string
}

export const TmbControlsRow: React.FC<TmbControlsRowProps> = ({ children, className = '' }) => (
    <div className={`tmb-controls-row ${className}`.trim()}>
        {children}
    </div>
)

interface TmbPillSelectProps {
    displayText: string
    children: ReactNode
    variant?: 'default' | 'question' | 'stratification' | 'multiselect'
    className?: string
    isOpen?: boolean
    onToggle?: (open: boolean) => void
    style?: React.CSSProperties
    infoTooltip?: string
}

export const TmbPillSelect: React.FC<TmbPillSelectProps> = ({
    displayText,
    children,
    variant = 'default',
    className = '',
    isOpen: controlledOpen,
    onToggle,
    style,
    infoTooltip,
}) => {
    const [internalOpen, setInternalOpen] = useState(false)
    const pillRef = useRef<HTMLDivElement>(null)

    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen
    const setOpen = onToggle || setInternalOpen

    useClickOutsideSingle(pillRef, () => setOpen(false), isOpen)

    const variantClass = variant !== 'default' ? `tmb-pill--${variant}` : ''

    return (
        <div
            className={`tmb-pill has-arrow ${variantClass} ${isOpen ? 'open' : ''} ${className}`.trim()}
            ref={pillRef}
            style={style}
            onClick={() => setOpen(!isOpen)}
        >
            {infoTooltip && (
                <span className="tmb-pill-info-icon" title={infoTooltip}>i</span>
            )}
            <div className="tmb-pill-display">
                {displayText}
            </div>
            <div
                className={`tmb-pill-dropdown ${isOpen ? 'show' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    )
}

interface TmbStratificationPillProps {
    value: string
    onChange: (value: string) => void
    className?: string
}

export const TmbStratificationPill: React.FC<TmbStratificationPillProps> = ({
    value,
    onChange,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const pillRef = useRef<HTMLDivElement>(null)

    useClickOutsideSingle(pillRef, () => setIsOpen(false), isOpen)

    const displayText = value === 'ExcludeNotSure'
        ? 'Exclude "Not Sure"\nHuddle Participation'
        : 'Include "Not Sure"\nHuddle Participation'

    return (
        <div
            className={`tmb-pill has-arrow tmb-pill--stratification ${isOpen ? 'open' : ''} ${className}`.trim()}
            ref={pillRef}
            onClick={() => setIsOpen(!isOpen)}
        >
            <div className="tmb-pill-display">
                {displayText}
            </div>
            <div
                className={`tmb-pill-dropdown ${isOpen ? 'show' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={`tmb-pill-dropdown-item ${value === 'ExcludeNotSure' ? 'selected' : ''}`}
                    onClick={() => { onChange('ExcludeNotSure'); setIsOpen(false) }}
                >
                    Exclude "Not Sure" Huddle Participation
                </div>
                <div
                    className={`tmb-pill-dropdown-item ${value === 'IncludeNotSure' ? 'selected' : ''}`}
                    onClick={() => { onChange('IncludeNotSure'); setIsOpen(false) }}
                >
                    Include "Not Sure" Huddle Participation
                </div>
            </div>
        </div>
    )
}

interface QuestionInfo {
    name: string
    code: string
    fullText: string
}

interface TmbQuestionPillProps {
    questions: QuestionInfo[]
    selectedQuestion: string
    onSelect: (questionName: string) => void
    className?: string
}

export const TmbQuestionPill: React.FC<TmbQuestionPillProps> = ({
    questions,
    selectedQuestion,
    onSelect,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const pillRef = useRef<HTMLDivElement>(null)

    useClickOutsideSingle(pillRef, () => setIsOpen(false), isOpen)

    const selectedData = questions.find(q => q.name === selectedQuestion)
    const displayText = selectedData?.code || 'Select question'

    return (
        <div
            className={`tmb-pill has-arrow tmb-pill--question ${isOpen ? 'open' : ''} ${className}`.trim()}
            ref={pillRef}
            onClick={() => setIsOpen(!isOpen)}
        >
            <div className="tmb-pill-display">
                {displayText}
            </div>
            <div
                className={`tmb-pill-dropdown tmb-question-dropdown ${isOpen ? 'show open' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="tmb-question-dropdown-inner">
                    {questions.map(q => (
                        <div
                            key={q.name}
                            className={`tmb-question-item ${q.name === selectedQuestion ? 'selected' : ''}`}
                            onClick={() => { onSelect(q.name); setIsOpen(false) }}
                        >
                            {q.code} - {q.fullText}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
